import React, { useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ScanLine, Loader2, ImagePlus, Camera } from "lucide-react";
import { toast } from "sonner";

/**
 * Reusable identity-document scanner.
 * Accepts one or more images (recto/verso CIN, permis, passeport...), sends each to
 * /api/ocr/identity and merges the extracted fields. Calls onExtract(merged) once done.
 *
 * Props:
 * - onExtract(data): callback with merged extracted object
 *   { nom_complet, telephone, email, adresse, date_naissance, lieu_naissance,
 *     numero_piece, type_piece, date_emission, date_expiration }
 * - testIdPrefix: data-testid prefix (default "id-scan")
 * - compact (bool): smaller layout (used inside dialogs)
 */
export default function IdentityScannerBox({ onExtract, testIdPrefix = "id-scan", compact = false }) {
  const [scanning, setScanning] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const cameraInputRef = useRef(null);
  const importInputRef = useRef(null);

  const mergeData = (acc, d) => {
    // Keep first non-empty value for each field
    const out = { ...acc };
    Object.entries(d || {}).forEach(([k, v]) => {
      if (v != null && v !== "" && (out[k] == null || out[k] === "")) {
        out[k] = v;
      }
    });
    return out;
  };

  const handleFiles = async (e) => {
    e.stopPropagation();
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setScanning(true);
    setProgress({ done: 0, total: files.length });

    let merged = {};
    let okCount = 0;
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/ocr/identity", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (data?.ok && data.data) {
          merged = mergeData(merged, data.data);
          okCount += 1;
        }
      } catch (err) {
        console.warn(`Scan failed for ${f.name}:`, err);
      } finally {
        setProgress({ done: i + 1, total: files.length });
      }
    }

    setScanning(false);
    setProgress({ done: 0, total: 0 });
    if (cameraInputRef.current) cameraInputRef.current.value = "";
    if (importInputRef.current) importInputRef.current.value = "";

    if (okCount === 0) {
      toast.error("Aucune donnée extraite. Vérifiez la qualité des photos.");
      return;
    }
    onExtract?.(merged);
    toast.success(
      files.length === 1
        ? "Pièce d'identité scannée"
        : `${okCount}/${files.length} photos analysées · données fusionnées`,
    );
  };

  return (
    <div
      data-testid={`${testIdPrefix}-box`}
      className={`rounded-sm border border-dashed border-klein/40 bg-klein/5 ${
        compact ? "px-3 py-2.5" : "px-4 py-3"
      } flex items-center gap-3`}
    >
      <div className="flex-shrink-0 w-9 h-9 rounded-sm bg-klein text-white flex items-center justify-center">
        {scanning ? <Loader2 size={16} className="animate-spin" /> : <ScanLine size={16} />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-bold leading-tight">Scanner pièce(s) d'identité</div>
        <div className="text-[11px] text-muted-foreground leading-tight">
          {scanning && progress.total > 0
            ? `Analyse ${progress.done}/${progress.total}…`
            : "CIN · Permis · Passeport — 1 ou plusieurs photos (recto + verso fusionnés par l'IA)."}
        </div>
      </div>
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        multiple
        capture="environment"
        onChange={handleFiles}
        className="hidden"
        data-testid={`${testIdPrefix}-camera-input`}
      />
      <input
        ref={importInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFiles}
        className="hidden"
        data-testid={`${testIdPrefix}-input`}
      />
      <div className="flex flex-col sm:flex-row gap-1.5">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={scanning}
          onClick={(e) => { e.stopPropagation(); cameraInputRef.current?.click(); }}
          data-testid={`${testIdPrefix}-camera-btn`}
          className="rounded-sm whitespace-nowrap"
          title="Prendre une photo avec la caméra"
        >
          <Camera size={14} className="mr-1" /> Scanner
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={scanning}
          onClick={(e) => { e.stopPropagation(); importInputRef.current?.click(); }}
          data-testid={`${testIdPrefix}-btn`}
          className="rounded-sm whitespace-nowrap"
          title="Importer depuis votre galerie ou vos fichiers"
        >
          {scanning ? (
            <>
              <Loader2 size={14} className="mr-1 animate-spin" /> Analyse…
            </>
          ) : (
            <>
              <ImagePlus size={14} className="mr-1" /> Importer
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
